import { Mapping } from 'api/MappingsAPI';
import { WORKFLOW_TRIGGERS } from 'modules/workflows';
import moment from 'moment';
import { generateId } from 'utils/helpers';
import { isUndefined } from 'utils/tsHelpers';
import { RETENTION_PERIOD_ERROR_MESSAGES } from '../constants';
import {
  CustomEvent,
  DeletionPolicy,
  MergedCustomEvent,
  RetentionPeriodsByEventName,
  UpdateDeletionPoliciesRequestPayload,
  WorkflowNodeReference,
} from '../types';

export const getEventNamesByRetentionPeriod = (deletionPolicies: DeletionPolicy[]) =>
  deletionPolicies
    .filter((pol) => pol.enabled) // Deletion policies are toggled on/off on the BE using this key. We should not show disabled retention periods.
    .reduce((acc: RetentionPeriodsByEventName, pol) => {
      const retentionPeriod = pol.ageInDays;
      const eventNames = pol.includeNamesJs?.includeNames || [];
      const eventNamesByAge = eventNames.reduce(
        (a, name) =>
          /**
          It is possible for the same custom event to appear on multiple deletion policies. The ES Groomer
          executes all deletion policies and so the shortest retention policy (which will always be executed first) 
          determines when the events will be deleted. Here we therefore ensure that we only display the 
          shortest retention period associated with each event.
         */
          !acc[name] || acc[name] > retentionPeriod ? { ...a, [name]: retentionPeriod } : a,
        {},
      );
      return { ...acc, ...eventNamesByAge };
    }, {});

export const formatIncludeNames = (eventNames: string[]) =>
  `{"includeNames":[${eventNames.map((e) => `"${e}"`).join(',')}]}`;

export const getRetentionPeriodErrorMessage = (num: number, radio: number): string => {
  const tenYears = 3650;

  if (radio === 999999 && num < 30) return RETENTION_PERIOD_ERROR_MESSAGES.MIN;
  if (num > tenYears) return RETENTION_PERIOD_ERROR_MESSAGES.MAX;
  return '';
};

export const formatRetentionPolicy = (age: number) => {
  if (age === -1) return 'Unlimited';

  return age % 365 === 0 ? `${age / 365} ${age / 365 === 1 ? 'year' : 'years'}` : `${age} days`;
};

export const formatUpdateDeletionPolicyToastMessage = (
  retentionPeriod: number,
  events: string[],
) => {
  const duration = formatRetentionPolicy(retentionPeriod);
  const names = events.join(', ').replace(/, ([^,]*)$/, ' and $1');

  return `Event retention period was updated to ${duration} for ${names}. Deletions may take a few days to process and be reflected in the volume count.`;
};

export const createUpdateDeletionPolicyPayload = (
  retentionPeriod: number,
  events: string[],
  projectId: number | undefined,
): UpdateDeletionPoliciesRequestPayload => {
  // Redundant check makes doubly sure we do not send retention of < 30
  const retention = retentionPeriod < 30 ? -1 : retentionPeriod;
  const enabled = retention !== -1;
  return {
    id: generateId(),
    projectId,
    updates: [
      {
        enabled,
        includeNames: events,
        retention,
      },
    ],
  };
};

export const createWarningMessage = (count: number, days: number, isLoading = false) => {
  const date = moment().subtract(days, 'days').format('MM/DD/YY');
  const formattedCount = new Intl.NumberFormat('en-US').format(count);
  const displayCount = isLoading || count === undefined ? '' : ` (around ${formattedCount})`;

  return `Be careful! By making this update, all events created before ${date} will be deleted today${displayCount}. Please check your list and journey references before proceeding.`;
};

/** get event from joinedEventData list, set default values for optional properties */
export const getEventData = (joinedEventData: Record<string, CustomEvent>, eventName: string) => {
  const defaultData = {
    dynamicLists: [],
    eventName,
    docCount: 0,
    lastSeen: 0,
    workflowAndNodes: [],
  };

  const currentData = joinedEventData?.[eventName] ?? {};

  return { ...defaultData, ...currentData };
};

/** get retention period as age in days with default value for undefined events */
export const getRetentionPeriod = (
  retentionPeriodsByEventName: { [key: string]: number },
  eventName: string,
): number => {
  const result = retentionPeriodsByEventName?.[eventName];
  return result ?? -1;
};

export const formatWorkflowTriggers = (workflowAndNodes: WorkflowNodeReference[] = []) => {
  return workflowAndNodes.filter((node) => {
    const isTrigger = node?.nodeClass === WORKFLOW_TRIGGERS.TRIGGERED_CUSTOM_EVENT;
    return isTrigger && !node.workflowArchived && !!node.workflowEnabled;
  });
};

/** Convert Mapping to MergeCusomEvent type */
export const convertMappingToMergedCustomEvent = (
  event: Mapping,
  joinedEventData: Record<string, CustomEvent>,
  retentionPeriodsByEventName: Record<string, number>,
): MergedCustomEvent => {
  const { isAllowed, isSaved, fieldName: eventName } = event;
  const {
    docCount,
    lastSeen,
    dynamicLists = [],
    workflowAndNodes = [],
  } = getEventData(joinedEventData, eventName);
  const retentionPolicy = getRetentionPeriod(retentionPeriodsByEventName, eventName);
  const listReferences = dynamicLists.length;
  const workflowReferences = workflowAndNodes.length;
  const workflowTriggers = formatWorkflowTriggers(workflowAndNodes);

  // both properties can be `undefined` which should be treated as `true`
  // only `false` should return `false`
  const allowed = isUndefined(isAllowed) || Boolean(isAllowed);
  const saved = isUndefined(isSaved) || Boolean(isSaved);

  return {
    allowed,
    docCount,
    eventName,
    lastSeen,
    listReferences,
    references: listReferences + workflowReferences,
    retentionPolicy,
    saved,
    workflowReferences,
    workflowTriggers,
  };
};
